import ExcelJS from 'exceljs'
import { splitCsvLine } from './productsExcel'
import { PROFILE_TYPE_COLOR } from './inventoryProfiles'
import type { ImportSchema, ImportTarget } from '@/api/inventoryApi'
import type { InventoryProfileType } from '@/types/inventory'

// ─── Reading a customer's file ───────────────────────────────────────────────
//
// Parsing only. What a column MEANS is the backend's job — it holds the ~120
// field definitions this has to resolve against, and duplicating that judgement
// here would give the wizard a second opinion that could disagree with the one
// that actually writes the rows.

/** A cell, in the shapes the import endpoints accept. */
export type ImportCell = string | number | boolean | null

export interface ParsedImportFile {
  headers: string[]
  rows: ImportCell[][]
  errors: string[]
}

/** Trailing empty columns and fully-blank rows carry no information. */
function isBlankCell(v: ImportCell): boolean {
  return v === null || v === '' || (typeof v === 'string' && v.trim() === '')
}

export function rowsToImportFile(rows: ImportCell[][]): ParsedImportFile {
  const headerRow = rows[0] ?? []
  // Trailing blank headings are Excel's, not the author's: a sheet reports a
  // used range wider than the data whenever a cell in it was ever touched.
  let width = headerRow.length
  while (width > 0 && isBlankCell(headerRow[width - 1])) width -= 1
  if (width === 0) return { headers: [], rows: [], errors: ['The first row has no column headings.'] }

  const headers = headerRow.slice(0, width).map((h) => String(h ?? '').trim())
  const body = rows
    .slice(1)
    .map((r) => Array.from({ length: width }, (_, i) => r[i] ?? null))
    .filter((r) => !r.every(isBlankCell))

  if (body.length === 0) return { headers, rows: [], errors: ['The file has no data rows.'] }
  return { headers, rows: body, errors: [] }
}

/**
 * Read one cell from a workbook.
 *
 * ExcelJS has already decoded a date-formatted cell into a `Date`, so the
 * backend's Excel-serial path never sees it — but a `Date` is not a shape the
 * endpoint accepts, and `String(date)` would produce a locale string it cannot
 * parse. ISO is the one form both sides agree on. Formula cells report their
 * cached `result`, which is the value the author was looking at.
 */
export function readCell(value: ExcelJS.CellValue): ImportCell {
  if (value === null || value === undefined) return null
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'string') {
    return value
  }
  if (typeof value === 'object') {
    // A formula cell reports the cached `result` — the value the author was
    // looking at when they saved. A hyperlink and rich text both carry their
    // display text, which is what the person typed.
    if ('result' in value) return readCell(value.result as ExcelJS.CellValue)
    if ('richText' in value) return value.richText.map((t) => t.text).join('')
    if ('text' in value) return String((value as { text: unknown }).text ?? '')
    if ('error' in value) return null
  }
  return String(value)
}

export async function parseImportWorkbook(buffer: ArrayBuffer): Promise<ParsedImportFile> {
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(buffer)
  const ws = wb.worksheets[0]
  if (!ws) return { headers: [], rows: [], errors: ['No worksheet found in the file.'] }

  const rows: ImportCell[][] = []
  for (let r = 1; r <= ws.rowCount; r += 1) {
    const row = ws.getRow(r)
    const cells: ImportCell[] = []
    for (let c = 1; c <= Math.max(row.cellCount, ws.columnCount); c += 1) {
      cells.push(readCell(row.getCell(c).value))
    }
    rows.push(cells)
  }
  return rowsToImportFile(rows)
}

export function parseImportCsv(text: string): ParsedImportFile {
  const lines = text.split(/\r\n|\r|\n/)
  // A trailing newline is not an empty row, but a blank line between records is
  // dropped rather than becoming a row of nulls the user has to explain.
  const rows = lines.filter((l) => l.trim().length > 0).map(splitCsvLine)
  if (rows.length === 0) return { headers: [], rows: [], errors: ['The file is empty.'] }
  return rowsToImportFile(rows)
}

/** Read whichever of the two formats this file is. */
export async function parseImportFile(file: {
  name: string
  arrayBuffer: () => Promise<ArrayBuffer>
  text: () => Promise<string>
}): Promise<ParsedImportFile> {
  if (/\.csv$/i.test(file.name)) return parseImportCsv(await file.text())
  if (/\.xlsx$/i.test(file.name)) return parseImportWorkbook(await file.arrayBuffer())
  return {
    headers: [],
    rows: [],
    errors: [`${file.name} is not a .xlsx or .csv file.`],
  }
}

// ─── Describing targets ──────────────────────────────────────────────────────

/** Every column a caller may map a header to, as one flat pick-list. */
export interface TargetOption {
  id: string
  target: ImportTarget
  /** What the user picks from the list. */
  label: string
  /** The category this belongs to, for grouping and colour. `null` = Material. */
  profileType: InventoryProfileType | null
}

export function targetId(target: ImportTarget): string {
  switch (target.kind) {
    case 'materialScalar':
      return `material.${target.key}`
    case 'profileName':
      return `profileName.${target.profileType}`
    case 'fieldValue':
      return `field.${target.profileType}.${target.key}`
  }
}

/**
 * Build the pick-list from the schema.
 *
 * Only the categories the server marks importable. The `actions` and `status`
 * categories exist in the enum but every def in them is a boolean that switches
 * a UI feature on or off — offering `Status · Available` next to the real
 * fields invited someone to map a stock quantity onto a checkbox, which is
 * exactly what happened.
 */
export function targetOptions(schema: ImportSchema): TargetOption[] {
  const options: TargetOption[] = []

  for (const scalar of schema.materialScalars) {
    options.push({
      id: `material.${scalar.key}`,
      target: { kind: 'materialScalar', key: scalar.key },
      label: `Material · ${scalar.label}`,
      profileType: null,
    })
  }

  for (const category of schema.categories) {
    if (!category.importable) continue
    if (category.assignable) {
      options.push({
        id: `profileName.${category.profileType}`,
        target: { kind: 'profileName', profileType: category.profileType },
        label: `${category.label} · profile name`,
        profileType: category.profileType,
      })
    }
    for (const field of category.fields) {
      options.push({
        id: `field.${category.profileType}.${field.key}`,
        target: { kind: 'fieldValue', profileType: category.profileType, key: field.key },
        label: `${category.label} · ${field.label}`,
        profileType: category.profileType,
      })
    }
  }

  return options
}

/** A target's human label, resolved against the schema. */
export function describeTarget(schema: ImportSchema, target: ImportTarget): string {
  return targetOptions(schema).find((o) => o.id === targetId(target))?.label ?? targetId(target)
}

/** The colour tokens for whatever a column writes to; Material columns are neutral. */
export function targetColor(target: ImportTarget | undefined): string {
  if (!target || target.kind === 'materialScalar') return 'bg-gray-100 text-gray-700 border-gray-200'
  return PROFILE_TYPE_COLOR[target.profileType].chip
}

// ─── Remembering a mapping ───────────────────────────────────────────────────
//
// A bare category label — `Attachments`, `Material Description` — names no
// field, and none of the categories has a self-named or single field, so there
// is no right answer to auto-pick. What there is instead is the answer this
// site gave last time: their supply master has the same headings every month,
// and re-mapping the same columns each time is the actual cost.
//
// Stored per tenant, per browser. It is a convenience, never a source of truth:
// a remembered target is applied only if it still exists in the schema, and any
// read or write failure is silent — a browser with storage disabled just does
// not remember.

const MAPPING_KEY = 'cgt.import.columnMapping'

function mappingStoreKey(tenantId: string): string {
  return `${MAPPING_KEY}.${tenantId}`
}

/** Header (normalised) → target id, as last chosen in this tenant. */
export function rememberedMappings(tenantId: string | null): Record<string, string> {
  if (!tenantId) return {}
  try {
    const raw = window.localStorage.getItem(mappingStoreKey(tenantId))
    const parsed: unknown = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, string>)
      : {}
  } catch {
    return {}
  }
}

export function rememberMappings(
  tenantId: string | null,
  headerToTarget: Record<string, string>,
): void {
  if (!tenantId) return
  try {
    const merged = { ...rememberedMappings(tenantId) }
    for (const [header, target] of Object.entries(headerToTarget)) {
      merged[normalizeMappingKey(header)] = target
    }
    window.localStorage.setItem(mappingStoreKey(tenantId), JSON.stringify(merged))
  } catch {
    // A browser that will not store is not a reason to fail an import.
  }
}

function normalizeMappingKey(header: string): string {
  return header.trim().toLowerCase().replace(/\s+/g, ' ')
}

/**
 * Pre-fill the columns this tenant has mapped before.
 *
 * Only columns the server could not resolve on its own, and only to targets the
 * schema still offers — a remembered field that has since been deleted must not
 * silently reappear as a mapping.
 */
export function applyRemembered(
  columns: { header: string; status: string }[],
  remembered: Record<string, string>,
  options: TargetOption[],
): Record<string, string> {
  const valid = new Set([...options.map((o) => o.id), 'ignore'])
  const out: Record<string, string> = {}
  for (const column of columns) {
    if (column.status === 'resolved') continue
    const target = remembered[normalizeMappingKey(column.header)]
    if (target && valid.has(target)) out[column.header] = target
  }
  return out
}

// ─── Profile decisions ───────────────────────────────────────────────────────

/** How the user answered one category's value-set. `''` = not answered yet. */
export type DecisionChoice = '' | 'attributes' | `reuse:${string}` | 'create'

export function decisionKey(profileType: InventoryProfileType, valueSetKey: string): string {
  return `${profileType}::${valueSetKey}`
}

/**
 * Pre-answer every decision, so the step is a review rather than an interrogation.
 *
 * A real 12-row supply master produces around forty of these, because each
 * material's own cost, owner and category are a distinct set of values — and a
 * set that appears on exactly one material is that material's data, not a
 * shared profile. Asking about each individually would put hundreds of
 * unanswered controls in front of someone importing 156 rows, and none of them
 * would be a question worth their time.
 *
 * So: reuse the profile when one matches exactly, and otherwise keep the values
 * on the material. Both are safe, and neither invents a named profile nobody
 * asked for. Promoting a set to a profile is still one control away on every
 * row — it is just no longer the thing standing between the user and an import.
 *
 * Existing answers are preserved: re-previewing after a mapping change must not
 * discard what the user already chose.
 */
export function withDefaults(
  decisions: {
    profileType: InventoryProfileType
    valueSetKey: string
    suggestedProfileId?: string
    missingProfileName?: string
  }[],
  current: Record<string, DecisionChoice>,
): Record<string, DecisionChoice> {
  const next = { ...current }
  for (const d of decisions) {
    const key = decisionKey(d.profileType, d.valueSetKey)
    if (next[key]) continue
    if (d.suggestedProfileId) {
      next[key] = `reuse:${d.suggestedProfileId}`
    } else if (d.missingProfileName) {
      // The file asked for a profile by name. Creating the profile it named is
      // what it was asking for, so that is the default — and the name is
      // already known, so nothing is left for the user to fill in.
      next[key] = 'create'
    } else {
      next[key] = 'attributes'
    }
  }
  return next
}

/** Names to pre-fill for the decisions defaulting to "create". */
export function defaultNames(
  decisions: { profileType: InventoryProfileType; valueSetKey: string; missingProfileName?: string }[],
  current: Record<string, string>,
): Record<string, string> {
  const next = { ...current }
  for (const d of decisions) {
    const key = decisionKey(d.profileType, d.valueSetKey)
    if (!next[key] && d.missingProfileName) next[key] = d.missingProfileName
  }
  return next
}

/**
 * A set of field values, written for someone reading a card.
 *
 * Field LABELS, not keys — `Storage Condition`, not `storageCondition` — and
 * capped, because a storage profile carries seventeen fields and a card listing
 * every one of them on a single line is not something anybody reads.
 */
export function describeValues(
  schema: ImportSchema | undefined,
  profileType: InventoryProfileType,
  values: Record<string, { value?: unknown }>,
  max = 6,
): { summary: string; hidden: number } {
  const fields = schema?.categories.find((c) => c.profileType === profileType)?.fields ?? []
  const labelFor = (key: string) => fields.find((f) => f.key === key)?.label ?? key

  const entries = Object.entries(values).map(([key, v]) => {
    const value = v?.value
    const text = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value ?? '')
    return `${labelFor(key)}: ${text}`
  })

  return {
    summary: entries.slice(0, max).join(' · '),
    hidden: Math.max(0, entries.length - max),
  }
}

/**
 * Which decisions are worth putting in front of someone.
 *
 * A value-set several materials share looks like a profile. A profile the file
 * named that we do not have is a decision by definition. A set carried by one
 * material and named by nobody is that material's own data — pre-answered and
 * folded away.
 */
export function splitDecisions<T extends { rows: number[]; missingProfileName?: string }>(
  decisions: T[],
) {
  const isNotable = (d: T) => d.rows.length > 1 || !!d.missingProfileName
  return {
    shared: decisions.filter(isNotable),
    perRow: decisions.filter((d) => !isNotable(d)),
  }
}

// ─── Building the template ───────────────────────────────────────────────────

/**
 * Header text for one column of the template.
 *
 * The plain label wherever it belongs to exactly one target, and the qualified
 * `Category · Field` form wherever it does not. A template that spelled every
 * column plainly would hand its own user the ambiguity step to resolve — on a
 * file we generated, where we already knew the answer.
 */
export function templateHeaderFor(
  schema: ImportSchema,
  option: TargetOption,
  claimCounts: Map<string, number>,
): string {
  const plain = plainLabelOf(schema, option)
  return (claimCounts.get(normalize(plain)) ?? 0) > 1 ? option.label : plain
}

/**
 * The heading for a column holding the NAME of a named profile.
 *
 * It must contain the word "profile": the server only reads a header as a
 * profile-name column when it says so, because a bare `Manufacturer` in a
 * customer's file holds a manufacturer's name rather than one of ours. Mirrors
 * `profileNameAliases` in `cgt-backend/src/inventory/import/import-schema.ts` —
 * a template that spelled these the old way would generate headers its own
 * importer could not resolve.
 *
 * Labels that already end in the word keep it: `Storage Profile`, not
 * `Storage Profile Profile`.
 */
export function profileNameHeader(categoryLabel: string): string {
  return /\bprofile\s*$/i.test(categoryLabel) ? categoryLabel : `${categoryLabel} Profile`
}

function plainLabelOf(schema: ImportSchema, option: TargetOption): string {
  // Destructured first: narrowing `option.target` inside a closure does not
  // survive into the callback, so `find` would see the un-narrowed union.
  const target = option.target
  if (target.kind === 'materialScalar') {
    return schema.materialScalars.find((s) => s.key === target.key)?.label ?? option.id
  }
  if (target.kind === 'profileName') {
    const label = schema.categories.find((c) => c.profileType === target.profileType)?.label
    return label ? profileNameHeader(label) : option.id
  }
  const category = schema.categories.find((c) => c.profileType === target.profileType)
  return category?.fields.find((f) => f.key === target.key)?.label ?? option.id
}

function normalize(v: string): string {
  return v.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/** How many targets answer to each plain label — anything above one is ambiguous. */
export function labelClaimCounts(schema: ImportSchema): Map<string, number> {
  const counts = new Map<string, number>()
  for (const option of targetOptions(schema)) {
    const key = normalize(plainLabelOf(schema, option))
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return counts
}

/**
 * A category field whose label duplicates a Material column.
 *
 * The seeded catalogue has `material_description.type` labelled "Material
 * Type", the same as the required `Material.materialType`. Offering both in one
 * template put two identically-named columns side by side, one of them
 * required — and the first real file filled in the wrong one, leaving every row
 * refused with "Material Type is required".
 *
 * The Material column wins, so the template carries exactly one. The picker
 * still offers the category field, so a deliberate mapping can still reach it.
 */
function duplicatesAMaterialColumn(schema: ImportSchema, option: TargetOption): boolean {
  if (option.target.kind !== 'fieldValue') return false
  const label = normalize(plainLabelOf(schema, option))
  return schema.materialScalars.some((s) => normalize(s.label) === label)
}

/** The columns the template offers, in the order it lays them out. */
export function templateColumns(
  schema: ImportSchema,
): { header: string; profileType: InventoryProfileType | null }[] {
  const options = targetOptions(schema).filter((o) => !duplicatesAMaterialColumn(schema, o))

  // Claims are counted over what the template actually CARRIES, so dropping the
  // duplicate lets the surviving column spell itself plainly: `Material Type`,
  // not `Material · Material Type`.
  const claims = new Map<string, number>()
  for (const option of options) {
    const key = normalize(plainLabelOf(schema, option))
    claims.set(key, (claims.get(key) ?? 0) + 1)
  }

  // Material columns first — they are what identifies a row — then each
  // category's profile-name column immediately ahead of its own fields, so a
  // reader can see which fields the named profile would otherwise supply.
  const ordered = [
    ...options.filter((o) => o.profileType === null),
    ...schema.categories
      .filter((c) => c.importable)
      .flatMap((c) => options.filter((o) => o.profileType === c.profileType)),
  ]
  return ordered.map((o) => ({
    header: templateHeaderFor(schema, o, claims),
    profileType: o.profileType,
  }))
}

/**
 * The blank import template, coloured by category.
 *
 * The colour legend is its own worksheet rather than a first row on the data
 * sheet. A legend row would put the headings on row 2, and every error this
 * feature reports is "row N" — a template that shifted the numbering by one
 * would make its own error messages point at the wrong line.
 */
export async function buildTemplateWorkbook(schema: ImportSchema): Promise<ArrayBuffer> {
  const wb = new ExcelJS.Workbook()
  wb.created = new Date()

  const sheet = wb.addWorksheet('Materials')
  const columns = templateColumns(schema)

  const header = sheet.getRow(1)
  columns.forEach((column, i) => {
    const cell = header.getCell(i + 1)
    cell.value = column.header
    const color = column.profileType
      ? PROFILE_TYPE_COLOR[column.profileType]
      : { argb: 'FFE5E7EB', inkArgb: 'FF374151' }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color.argb } }
    cell.font = { bold: true, color: { argb: color.inkArgb } }
    cell.alignment = { vertical: 'middle', wrapText: true }
    sheet.getColumn(i + 1).width = Math.min(Math.max(column.header.length + 4, 14), 32)
  })
  header.height = 30
  sheet.views = [{ state: 'frozen', ySplit: 1 }]

  const key = wb.addWorksheet('Colour key')
  key.getColumn(1).width = 28
  key.getColumn(2).width = 64
  key.getRow(1).getCell(1).value = 'Category'
  key.getRow(1).getCell(2).value = 'Fill in the columns you have. Delete the ones you do not.'
  key.getRow(1).font = { bold: true }

  schema.categories
    .filter((c) => c.importable)
    .forEach((category, i) => {
      const row = key.getRow(i + 2)
      const cell = row.getCell(1)
      const color = PROFILE_TYPE_COLOR[category.profileType]
      cell.value = category.label
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color.argb } }
      cell.font = { bold: true, color: { argb: color.inkArgb } }
      row.getCell(2).value = category.assignable
        ? `Put a profile name in "${profileNameHeader(category.label)}" — an existing one, or a new name to create. ` +
          'Or leave it blank and fill this category’s field columns instead.'
        : 'Values here are kept on each material — this category has no shared profile.'
    })

  return wb.xlsx.writeBuffer() as Promise<ArrayBuffer>
}
